import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_information_filled } from '@repo/icons/index'
import { useState } from 'react'

import { HACKLE_FLAG_KEY, useFeatureFlag } from '../../../../helpers/hackleHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { n_common, n_review } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import {
  LESSON_EVALUATION_TUTOR_BAD_GRAY,
  LESSON_EVALUATION_TUTOR_BAD_PRIMARY,
  LESSON_EVALUATION_TUTOR_GOOD_GRAY,
  LESSON_EVALUATION_TUTOR_GOOD_PRIMARY,
  LESSON_EVALUATION_TUTOR_SOSO_GRAY,
  LESSON_EVALUATION_TUTOR_SOSO_PRIMARY,
} from '../../../../modules/images'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import useEvaluationStore from '../../../../store/useEvaluationStore'
import { Tutor } from './EvaluationModalBody'

interface IPreferList {
  status: string
  beforeImg: string
  afterImg: string
  value: number
}
const preferList: IPreferList[] = [
  {
    status: 'bad',
    beforeImg: LESSON_EVALUATION_TUTOR_BAD_GRAY,
    afterImg: LESSON_EVALUATION_TUTOR_BAD_PRIMARY,
    value: 1,
  },
  {
    status: 'soso',
    beforeImg: LESSON_EVALUATION_TUTOR_SOSO_GRAY,
    afterImg: LESSON_EVALUATION_TUTOR_SOSO_PRIMARY,
    value: 2,
  },
  {
    status: 'good',
    beforeImg: LESSON_EVALUATION_TUTOR_GOOD_GRAY,
    afterImg: LESSON_EVALUATION_TUTOR_GOOD_PRIMARY,
    value: 3,
  },
]

const enum STATUS {
  BAD = 'bad',
  SOSO = 'soso',
  GOOD = 'good',
}

type IHoverImage = {
  beforeImg: string
  afterImg: string
  selected: boolean
}

const HoverImage = ({ beforeImg, afterImg, selected }: IHoverImage) => {
  const [isHover, setIsHover] = useState(false)

  const handleMouseEnter = () => {
    setIsHover(true)
  }

  const handleMouseLeave = () => {
    setIsHover(false)
  }

  return (
    <div
      className={`flex h-[32px] w-[32px] cursor-pointer select-none items-center justify-center ${
        isHover || selected ? 'rounded-full bg-purple-50' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        className="h-[24px] w-[24px]"
        src={isHover || selected ? afterImg : beforeImg}
        alt={isHover || selected ? 'before' : 'after'}
      />
    </div>
  )
}

type PreferenceItem = {
  value: number
  beforeImg: string
  afterImg: string
}

type SelectPreferenceSectionProps = {
  wrapperClx: string
  setSelected: (value: number) => void
  selected: number
  list: PreferenceItem[]
  setHover?: any
}

export const SelectPreferenceSection = ({
  wrapperClx,
  setSelected,
  selected,
  list,
  setHover,
}: SelectPreferenceSectionProps) => {
  const handleMouseEnter = (value: number) => {
    if (setHover) {
      setHover(value)
    }
  }

  const handleMouseLeave = () => {
    if (setHover) {
      setHover(selected)
    }
  }

  return (
    <div className={wrapperClx}>
      {list.map((item, i) => {
        return (
          <div
            key={i}
            onClick={() => {
              setSelected(item.value)
            }}
            onMouseEnter={() => handleMouseEnter(item.value)}
            onMouseLeave={handleMouseLeave}
          >
            <HoverImage selected={selected === item.value} beforeImg={item.beforeImg} afterImg={item.afterImg} />
          </div>
        )
      })}
    </div>
  )
}
interface ILessonEvaluationStep3 {
  next: () => void
  tutorData: Tutor
  lessonId: number
  sendClickEventToGTM: (buttonName: string) => void
  onClose: () => void
}
const EvaluationStep3 = ({ next, tutorData, lessonId, sendClickEventToGTM, onClose }: ILessonEvaluationStep3) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const [hoverPreference, setHoverPreference] = useState<number>(0)
  const { tutorPreference, setTutorPreference } = useEvaluationStore((state) => ({
    tutorPreference: state.tutorPreference,
    setTutorPreference: state.setTutorPreference,
  }))
  const isClassroom = router?.asPath?.includes('/lessons/classroom/') ?? false
  const moveLessonCompletePage = useFeatureFlag(HACKLE_FLAG_KEY.MOVE_LESSON_COMPLETE_PAGE)
  const isAvailableNext = tutorPreference && tutorPreference > 0

  const getPreferStatusDesc = (preference: number, hoverPreference: number) => {
    let targetPreference = hoverPreference !== null ? hoverPreference : preference

    if (targetPreference < 0 || targetPreference > 5) {
      targetPreference = 0 // 평가 범위를 벗어난 경우 0으로 설정
    }

    const preferenceTexts = [
      t(n_review.step_3.rate_placeholder),
      t(n_review.step_3.rate_1),
      t(n_review.step_3.rate_2),
      t(n_review.step_3.rate_3),
    ]

    return preferenceTexts[targetPreference] || preferenceTexts[0]
  }

  const handleTutorPreference = (preference: number) => {
    setTutorPreference(preference)
  }

  const handleNextButton = (isAvailableNext: boolean | number) => {
    if (isAvailableNext) {
      next()
      sendClickEventToGTM('평가 완료')
    }
  }

  const handleLaterButton = () => {
    onClose()
    sendClickEventToGTM('나중에 하기')

    if (isClassroom) {
      if (moveLessonCompletePage) {
        router.push(urls.student.newPortal.lessons.complete + '?lesson_id=' + lessonId)
      } else {
        router.push(urls.student.newPortal.lessons.reviews.home)
      }
    }
  }

  return (
    <div className="flex h-[calc(100vh_-_100px)] flex-col justify-between sm:h-full">
      <div className="Headline2-24Bd keep-all ">{t(n_review.step_3.title(tutorData.name))}</div>
      <div className="Body2-14Bd mt-[24px] flex flex-row items-center gap-[4px] rounded-[4px] bg-blue-50 p-[12px]">
        <QIcon_information_filled color={COLORS.BLUE400} width={20} height={20} />
        <div className="">{t(n_review.step_3.callout)}</div>
      </div>
      <div className="flex h-full flex-col justify-between">
        <div className="mt-[12px] flex h-[332px] flex-col items-center justify-center">
          <SelectPreferenceSection
            list={preferList}
            selected={tutorPreference}
            setHover={(value: number) => setHoverPreference(value)}
            setSelected={handleTutorPreference}
            wrapperClx="flex flex-row gap-[16px]"
          />
          <div className="Body2-14Md mt-[8px]" style={{ textAlign: 'center' }}>
            {getPreferStatusDesc(tutorPreference, hoverPreference)}
          </div>
        </div>

        <div className="mt-[-2px] flex justify-end gap-[8px]">
          <div className="hidden sm:block sm:h-[46px] sm:w-[112px]">
            <DSButton
              disabled={!isAvailableNext}
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.OUTLINE}
              onClick={() => handleLaterButton()}
            >
              <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
            </DSButton>
          </div>
          <div className="mb-[34px] w-full sm:mb-[0px] sm:h-[46px] sm:w-[112px]">
            <DSButton
              disabled={!isAvailableNext}
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.SOLID}
              onClick={() => handleNextButton(isAvailableNext)}
              className="border-1 border-transparent"
            >
              <DSButton.Label>{t(n_common.btn_next)}</DSButton.Label>
            </DSButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EvaluationStep3
